// View 8: Document Upload

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentUpload } from '@/components/forms';
import { useOrderStore } from '@/stores/orderStore';
import { submitCheckout, type UploadProgress } from '@/services/checkoutService';
import { track } from '@/utils/analytics';
import { logger } from '@/utils/logger';
import { FILE_UPLOAD_LIMITS } from '@/utils/constants';
import { assembleCheckoutData } from '@/utils/checkoutAssembly';

type SubmitStatusStage = 'starting' | 'slowerConnection' | 'stillWorking' | 'takingLonger';

const SUBMIT_STATUS_CONTENT: Record<SubmitStatusStage, {
  title: string;
  detail: string;
  tone: 'info' | 'warning';
  showSupport: boolean;
}> = {
  starting: {
    title: 'Uploading your documents...',
    detail: 'Please keep this page open while we upload your files and submit your order.',
    tone: 'info',
    showSupport: false,
  },
  slowerConnection: {
    title: 'Still uploading...',
    detail: 'This can take up to 90 seconds on slower connections.',
    tone: 'info',
    showSupport: false,
  },
  stillWorking: {
    title: 'Still working...',
    detail: 'We are securely uploading your documents and submitting your order.',
    tone: 'info',
    showSupport: false,
  },
  takingLonger: {
    title: 'Taking longer than usual...',
    detail: 'Please do not tap submit again. Your order may still finish processing.',
    tone: 'warning',
    showSupport: true,
  },
};

export function DocumentUploadView() {
  const navigate = useNavigate();
  const {
    userId,
    address,
    personalInfo,
    configuredPlans,
    selectedServices,
    appFields,
    uploadFlags,
    onitPlan,
    onitSelected,
    setConfirmationId,
    setElectricityCompletionLink,
  } = useOrderStore();
  const [dlFile, setDlFile] = useState<File | null>(null);
  const [leaseFile, setLeaseFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [submitStage, setSubmitStage] = useState<SubmitStatusStage>('starting');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  // When true, resubmitting could duplicate the order — the button stays locked.
  const [submitLocked, setSubmitLocked] = useState(false);
  const submitInFlightRef = useRef(false);
  const submitStatus = SUBMIT_STATUS_CONTENT[submitStage];

  // Detect duplicate files across both upload slots
  useEffect(() => {
    if (dlFile && leaseFile && dlFile.name === leaseFile.name && dlFile.size === leaseFile.size) {
      setDuplicateWarning(true);
    } else {
      setDuplicateWarning(false);
    }
  }, [dlFile, leaseFile]);

  // Compute combined file size and check against limit
  const combinedSizeInfo = useMemo(() => {
    const totalBytes = (dlFile?.size ?? 0) + (leaseFile?.size ?? 0);
    const totalMB = totalBytes / (1024 * 1024);
    const isOverLimit = totalMB > FILE_UPLOAD_LIMITS.MAX_COMBINED_FILES_MB;
    return { totalMB, isOverLimit };
  }, [dlFile, leaseFile]);

  // Track analytics when combined size warning is shown
  useEffect(() => {
    if (combinedSizeInfo.isOverLimit) {
      track('checkout_documents_too_large', {
        total_mb: combinedSizeInfo.totalMB.toFixed(1),
        dl_size: dlFile?.size ?? 0,
        lease_size: leaseFile?.size ?? 0,
      });
    }
  }, [combinedSizeInfo.isOverLimit, combinedSizeInfo.totalMB, dlFile?.size, leaseFile?.size]);

  useEffect(() => {
    if (!isSubmitting) {
      setSubmitStage('starting');
      return;
    }

    setSubmitStage('starting');

    const slowerConnectionTimeout = window.setTimeout(() => setSubmitStage('slowerConnection'), 10_000);
    const stillWorkingTimeout = window.setTimeout(() => setSubmitStage('stillWorking'), 30_000);
    const takingLongerTimeout = window.setTimeout(() => setSubmitStage('takingLonger'), 60_000);

    return () => {
      window.clearTimeout(slowerConnectionTimeout);
      window.clearTimeout(stillWorkingTimeout);
      window.clearTimeout(takingLongerTimeout);
    };
  }, [isSubmitting]);

  const handleSubmit = async () => {
    if (isSubmitting || submitInFlightRef.current) return;
    if (!userId || !address || !personalInfo) return;
    if (uploadFlags.isDLUpload && !dlFile) return;
    const isRenting = personalInfo.rentOrOwn === 'Rent';
    const needsLeaseDoc = (isRenting && uploadFlags.isLeaseUpload) || (!isRenting && uploadFlags.isOwnUpload);
    if (needsLeaseDoc && !leaseFile) return;

    if (submitLocked) return;

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setError(null);
    setIsSubmissionError(false);
    setUploadProgress(null);

    try {
      const assembled = assembleCheckoutData({
        address,
        personalInfo,
        configuredPlans,
        selectedServices,
        appFields,
        onitPlan,
        onitSelected,
        requireAppFields: true,
      });

      if (!assembled.ok) {
        logger.error({ area: 'checkout' }, assembled.logMessage);
        setError(assembled.error);
        setIsSubmitting(false);
        return;
      }

      assembled.warnings.forEach((warning) => {
        logger.warn({ area: 'checkout', userId }, warning, {
          serviceStartDateSelection: assembled.serviceStartDateSelection,
          selectedServices,
          configuredPlans,
        });
      });

      logger.info(
        { area: 'checkout', userId },
        `Checkout state: dob=set, configuredPlans=${configuredPlans.length}, serviceStartDates=${Object.keys(assembled.serviceStartDateSelection).length}, appFields=${appFields.length}`,
        {
          serviceStartDateSelection: assembled.serviceStartDateSelection,
          selectedServices,
          configuredPlans,
          appFieldCount: appFields.length,
          uploadFlags,
        }
      );

      const result = await submitCheckout(
        userId,
        assembled.checkoutData,
        {
          dlFile: uploadFlags.isDLUpload ? dlFile ?? undefined : undefined,
          rentFile: isRenting && uploadFlags.isLeaseUpload && leaseFile ? leaseFile : undefined,
          ownFile: !isRenting && uploadFlags.isOwnUpload && leaseFile ? leaseFile : undefined,
        },
        setUploadProgress
      );

      if (result.success && result.orderId) {
        setConfirmationId(result.orderId);
        setElectricityCompletionLink(result.electricityCompletionLink || null);
        const serviceTypes = selectedServices.map(s => s.type);
        track('checkout_documents_uploaded', {
          zip: address?.zip,
          flow_type: useOrderStore.getState().flowType,
          has_dl: !!dlFile,
          has_lease: !!leaseFile,
          dl_required: uploadFlags.isDLUpload,
          lease_required: uploadFlags.isLeaseUpload || uploadFlags.isOwnUpload,
          rent_or_own: personalInfo?.rentOrOwn,
        });
        track('checkout_order_complete', {
          confirmation_id: result.orderId,
          zip: address?.zip,
          flow_type: useOrderStore.getState().flowType,
          services: serviceTypes,
          has_water: serviceTypes.includes('water'),
          has_electricity: serviceTypes.includes('electricity'),
          has_internet: serviceTypes.includes('internet'),
          total_services: serviceTypes.length,
          onit_selected: onitSelected,
          onit_available: !!onitPlan,
          source: 'document_submit',
        });
        navigate('/confirmation');
      } else {
        let displayError = result.error || 'Checkout failed. Please try again.';
        if (displayError.includes('Invalid file type')) {
          displayError = 'One of your files is in an unsupported format. Please use JPEG, PNG, GIF, HEIC, or PDF.';
        }
        // The order may already exist, or resubmitting the same data will fail the
        // same way — either way, keep the user off the submit button.
        if (result.mayHaveSucceeded || result.needsSupport) {
          setSubmitLocked(true);
        }
        setIsSubmissionError(true);
        setError(displayError);
        // Detailed error tracked in checkoutService via checkout_api_error
        track('checkout_error', {
          error_context: 'document_upload',
          error_message: result.error || 'Checkout failed',
          may_have_succeeded: !!result.mayHaveSucceeded,
          needs_support: !!result.needsSupport,
        });
      }
    } catch (err) {
      logger.error({ area: 'checkout' }, 'Error submitting checkout', err instanceof Error ? err : undefined);
      setIsSubmissionError(true);
      setError('An unexpected error occurred. Please try again.');
      track('checkout_error', {
        error_context: 'document_upload_exception',
        error_message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  return (
    <section className="view" style={{ display: 'block' }}>
      <div className="container">
        <h1 className="text--center mb-3">One last thing...</h1>
        <p className="text--center mb-6">We need a couple of documents to complete your service setup</p>

        <div className="documents-required-container">
          {/* Driver's License Upload */}
          {uploadFlags.isDLUpload && (
            <div id="driver-license-upload" className="mb-5">
              <DocumentUpload
                id="drivers-license"
                value={dlFile}
                onChange={setDlFile}
                label="Driver's License"
                required
                accept="image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp,.pdf"
                maxSizeMB={FILE_UPLOAD_LIMITS.MAX_SINGLE_FILE_MB}
              />
            </div>
          )}

          {/* Lease/Ownership Document Upload — only show when relevant to rent/own status */}
          {((personalInfo?.rentOrOwn === 'Rent' && uploadFlags.isLeaseUpload) || (personalInfo?.rentOrOwn !== 'Rent' && uploadFlags.isOwnUpload)) && (
            <div id="lease-document-upload" className="mb-5">
              <DocumentUpload
                id="lease-document"
                value={leaseFile}
                onChange={setLeaseFile}
                label="Lease or Ownership Document"
                accept="image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp,.pdf"
                maxSizeMB={FILE_UPLOAD_LIMITS.MAX_SINGLE_FILE_MB}
              />
            </div>
          )}
        </div>

        {/* Combined Size Warning */}
        {combinedSizeInfo.isOverLimit && (
          <div className="warning-notice mb-4">
            <svg className="warning-notice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <div className="warning-notice__content">
              <p className="warning-notice__title">Files too large to upload</p>
              <p className="warning-notice__text">
                Your documents total {combinedSizeInfo.totalMB.toFixed(1)} MB, which exceeds the {FILE_UPLOAD_LIMITS.MAX_COMBINED_FILES_MB} MB upload limit.
                Please replace one or both files with smaller versions.
              </p>
            </div>
          </div>
        )}

        {/* Duplicate File Warning */}
        {duplicateWarning && (
          <div className="warning-notice mb-4">
            <svg className="warning-notice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <div className="warning-notice__content">
              <p className="warning-notice__title">Same file detected for both documents</p>
              <p className="warning-notice__text">
                Your city reviews these documents to verify your identity and residence.
                Orders cannot be processed without proper documentation &mdash; please make sure
                you upload a different document for each slot.
              </p>
            </div>
          </div>
        )}

        {/* Important Notice */}
        <div className="info-notice mb-6">
          <svg className="info-notice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <div className="info-notice__content">
            <p className="info-notice__title">Important: These documents are reviewed</p>
            <p className="info-notice__text">
              Your city reviews these documents to verify your identity and residence.
              Orders cannot be processed without valid documentation &mdash; please make sure
              each document is legible and correct.
            </p>
          </div>
        </div>

        {/* Submission Status */}
        {isSubmitting && (
          <div
            className={`submission-status submission-status--${submitStatus.tone} mb-4`}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="spinner-small submission-status__spinner" aria-hidden="true"></span>
            <div className="submission-status__content">
              <p className="submission-status__title">
                {uploadProgress && uploadProgress.attempt > 1
                  ? 'Connection dropped — retrying your upload...'
                  : submitStatus.title}
              </p>

              {/* Real upload progress beats a spinner: on slow mobile connections a
                  stalled-looking spinner is the main reason people give up. */}
              {uploadProgress && uploadProgress.percent < 100 ? (
                <>
                  <div
                    className="upload-progress"
                    role="progressbar"
                    aria-valuenow={uploadProgress.percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Document upload progress"
                  >
                    <div
                      className="upload-progress__bar"
                      style={{ width: `${uploadProgress.percent}%` }}
                    />
                  </div>
                  <p className="submission-status__text">
                    {uploadProgress.percent}% uploaded
                    {uploadProgress.total > 0 && (
                      <> &mdash; {(uploadProgress.loaded / (1024 * 1024)).toFixed(1)} of {(uploadProgress.total / (1024 * 1024)).toFixed(1)} MB</>
                    )}
                  </p>
                </>
              ) : (
                <p className="submission-status__text">{submitStatus.detail}</p>
              )}

              {uploadProgress && uploadProgress.percent >= 100 && (
                <p className="submission-status__text">
                  Upload complete &mdash; finishing your order...
                </p>
              )}

              <p className="submission-status__text submission-status__text--muted">
                Please keep this page open while we finish.
              </p>
              {submitStatus.showSupport && (
                <p className="submission-status__text">
                  Need help right away? Call{' '}
                  <a href="tel:1-888-300-6961" className="submission-status__phone">1-888-300-6961</a>.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-notice mb-4">
            {isSubmissionError && !submitLocked && (
              <p className="error-notice__text">
                We're sorry — something went wrong submitting your order.
              </p>
            )}
            <p className="error-notice__detail">{error}</p>
            {isSubmissionError && !submitLocked && (
              <p className="error-notice__text">
                Please try again, or call us at{' '}
                <a href="tel:1-888-300-6961" className="error-notice__phone">1-888-300-6961</a>
                {' '}for immediate help.
              </p>
            )}
            {submitLocked && (
              <p className="error-notice__text">
                Call us at{' '}
                <a href="tel:1-888-300-6961" className="error-notice__phone">1-888-300-6961</a>
                {' '}and we will sort this out without you having to start over.
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-row">
          <button type="button" className="btn btn--back" onClick={() => navigate(-1)} disabled={isSubmitting}>
            GO BACK
          </button>
          <button
            type="button"
            className="btn"
            onClick={handleSubmit}
            disabled={(uploadFlags.isDLUpload && !dlFile) || (((personalInfo?.rentOrOwn === 'Rent' && uploadFlags.isLeaseUpload) || (personalInfo?.rentOrOwn !== 'Rent' && uploadFlags.isOwnUpload)) && !leaseFile) || isSubmitting || combinedSizeInfo.isOverLimit || submitLocked}
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT ORDER'}
          </button>
        </div>
      </div>
    </section>
  )
}
